import apiClient from '@/services/api-client'

export interface Friend {
  id: string
  friendId: string
  status: string
  createdAt: string
  updatedAt: string
  friend: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  }
}

export interface PendingRequest {
  id: string
  userId: string
  status: string
  createdAt: string
  updatedAt: string
  user: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  }
}

export interface SentRequest {
  id: string
  userId: string
  friendId: string
  status: string
  createdAt: string
  updatedAt: string
  friend: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  }
}

export interface InviteCodeResponse {
  code: string
  url: string
}

export interface InviteValidationResponse {
  valid: boolean
  inviterName?: string
  inviterId?: string
  alreadyFriends?: boolean
  requestPending?: boolean
  requestSent?: boolean
  error?: string
}

export interface LeaderboardEntry {
  rank: number
  userId: string
  firstName: string | null
  lastName: string | null
  isCurrentUser: boolean
  performanceMetric: {
    value: number
    type: 'streak' | 'stars'
    label: string  // "5 day streak" or "8 stars"
  }
  avatar?: {
    initials: string
    backgroundColor: string
  }
  hasPlayed: boolean // NEW: indicates if user has activity for this date/game
}

export interface LeaderboardResponse {
  data: LeaderboardEntry[]
  metadata: {
    date: string
    gameId: string
    gameTitle: string
    totalFriends: number
  }
}

const friendsApi = {
  // Get all friends
  getFriends: async (): Promise<Friend[]> => {
    const response = await apiClient.get<{ friends: Friend[] }>('/api/v1/friends')
    return response.friends
  },

  // Get pending friend requests
  getPendingRequests: async (): Promise<PendingRequest[]> => {
    const response = await apiClient.get<{ requests: PendingRequest[] }>('/api/v1/friends/requests')
    return response.requests
  },

  // Get sent friend requests
  getSentRequests: async (): Promise<SentRequest[]> => {
    const response = await apiClient.get<{ requests: SentRequest[] }>('/api/v1/friends/requests/sent')
    return response.requests
  },

  // Get user's invite code
  getInviteCode: async (): Promise<InviteCodeResponse> => {
    const response = await apiClient.get<InviteCodeResponse>('/api/v1/friends/invite-code')
    return response
  },

  // Validate an invite code (public endpoint)
  validateInviteCode: async (code: string): Promise<InviteValidationResponse> => {
    const response = await apiClient.get<InviteValidationResponse>(`/api/v1/friends/invite/${code}`)
    return response
  },

  // Send a friend request
  sendFriendRequest: async (friendId: string): Promise<void> => {
    await apiClient.post('/api/v1/friends/request', { friendId })
  },

  // Accept a friend request
  acceptFriendRequest: async (requesterId: string): Promise<void> => {
    await apiClient.put('/api/v1/friends/request/accept', { requesterId })
  },

  // Reject a friend request
  rejectFriendRequest: async (requesterId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/friends/request/${requesterId}`)
  },

  // Remove a friend
  removeFriend: async (friendId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/friends/${friendId}`)
  },

  // Block a user
  blockUser: async (userId: string): Promise<void> => {
    await apiClient.post('/api/v1/friends/block', { userId })
  },

  // Get friends leaderboard
  getLeaderboard: async (date: string, gameId: string): Promise<LeaderboardResponse> => {
    // Validate date format
    if (!date || !date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const error = new Error('Invalid date format. Expected YYYY-MM-DD')
      Object.assign(error, { status: 400, code: 'INVALID_DATE_FORMAT' })
      throw error
    }

    // Validate gameId
    if (!gameId || (!gameId.match(/^[a-zA-Z0-9_]+$/) && gameId !== 'all')) {
      const error = new Error('Invalid game ID format')
      Object.assign(error, { status: 400, code: 'INVALID_GAME_ID' })
      throw error
    }

    const response = await apiClient.get<LeaderboardResponse>(
      `/api/v1/friends/leaderboard?date=${encodeURIComponent(date)}&gameId=${encodeURIComponent(gameId)}`
    )
    return response
  }
}

export default friendsApi
export const { validateInviteCode, getLeaderboard } = friendsApi